/**
 * パス上を文字が流れるマーキー
 *
 * @description textPathだと文字が波の傾きに沿って回転せず、SVGごと平行移動させても
 *              「パスの上を進んでいる」ようには見えない。1文字ずつパス上の座標と接線角度に
 *              置き直して、オフセットだけを進めることで波をなぞらせる。
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

// 1周にかける秒数。パス長から速度を逆算するのでパスを差し替えても体感速度が変わらない
const LOOP_SEC = 60;

// 座標表の刻み幅（パスのユーザー単位）。細かくするほど滑らかだが表が大きくなる
const SAMPLE_RES = 0.5;

// タブ復帰時に一気に進まないよう、1フレームで進める秒数に上限を置く
const MAX_DELTA = 0.1;

// SVGのデザイン上の高さ。実寸との比でデザイン座標を実pxへ直す
const DESIGN_SVG_H = 120;

// 画面に留める位置。デザインで帯が置かれている高さと同じ
const DESIGN_STICK_TOP = 238;

// ロゴの手前にこれだけ残して止める。すれ違わせないための余白
const DESIGN_LOGO_GAP = 40;

// 目標へ寄せる割合。1で即座に着く。小さいほど後ろに残って見える
const LAG_PER_FRAME = 0.06;

// 止まってから消えきるまでに使う高さ。画面の高さに対する割合
const FADE_RANGE = 0.3;

// PCの下限。これ未満はSPのstickyに任せる
const PC_MIN_WIDTH = 1025;

/**
 * 0..1 に収める
 *
 * @param v 値
 * @returns 収めた値
 */
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * cubic-bezier(0.08, 0.7, 0.58, 1) を二分法で解く
 *
 * @description サイト共通のイージング。ここだけのためにgsapを積むと重いので直に解く
 * @param t 0..1 の進行
 * @returns イージング後の値
 */
const ease = (t: number): number => {
    if (t <= 0) {
        return 0;
    }

    if (t >= 1) {
        return 1;
    }

    const [x1, y1, x2, y2] = [0.08, 0.7, 0.58, 1];
    const bx = (u: number): number => 3 * (1 - u) * (1 - u) * u * x1 + 3 * (1 - u) * u * u * x2 + u * u * u;
    const by = (u: number): number => 3 * (1 - u) * (1 - u) * u * y1 + 3 * (1 - u) * u * u * y2 + u * u * u;
    let lo = 0;
    let hi = 1;

    for (let i = 0; i < 20; i += 1) {
        const mid = (lo + hi) / 2;

        if (bx(mid) < t) {
            lo = mid;
        } else {
            hi = mid;
        }
    }

    return by((lo + hi) / 2);
};

type Runner = (delta: number) => void;

/**
 * SVG1枚ぶんのランナーを組む
 *
 * @param svg data-marquee-text と data-marquee-chars を持つSVG
 * @returns 経過秒を受け取って文字位置を更新する関数。組めなければ null
 */
const buildRunner = (svg: SVGSVGElement): Runner | null => {
    const unit = svg.dataset.marqueeText ?? '';
    const path = svg.querySelector<SVGPathElement>('defs path');
    const box = svg.querySelector<SVGGElement>('[data-marquee-chars]');

    if (unit === '' || !path || !box) {
        return null;
    }

    const total = path.getTotalLength();
    const glyphs = [...unit].length;

    // Cormorantはプロポーショナルなので、総長を文字数で均等割りすると字送りが合わず文字が重なる。
    // 1周ぶんを実際に組んで各文字の中心位置を測り、その間隔をパス上の距離として使う
    const probe = document.createElementNS(SVG_NS, 'text');
    probe.setAttribute('class', box.getAttribute('class') ?? '');
    probe.textContent = unit;
    svg.appendChild(probe);
    const unitWidth = probe.getComputedTextLength() || 1;
    const unitChars = [...unit];
    const origin = probe.getExtentOfChar(0).x;
    // getExtentOfChar はカーニング込みの実位置を返す。text-anchor:middle に合わせて中心を取る
    const centers = unitChars.map((_, i) => {
        const extent = probe.getExtentOfChar(i);
        return extent.x + extent.width / 2 - origin;
    });
    probe.remove();

    // 総長に収まる周回数だけ並べる。roundで切り上がると1周ぶん多く詰めることになり、
    // 総長へ合わせるfitが1を大きく下回って字送りが潰れる（2周を詰めると約24%圧縮された）
    const laps = Math.max(1, Math.floor(total / unitWidth));
    // 並べた実距離を総長へ均等に伸縮させて、ループの継ぎ目で間隔が飛ばないようにする
    const fit = total / (laps * unitWidth);
    const chars: string[] = [];
    const dists: number[] = [];

    for (let lap = 0; lap < laps; lap += 1) {
        for (let i = 0; i < glyphs; i += 1) {
            chars.push(unitChars[i]);
            dists.push((centers[i] + lap * unitWidth) * fit);
        }
    }

    const nodes = chars.map((char) => {
        const node = document.createElementNS(SVG_NS, 'text');
        // 回転の中心を文字の中心に置く。ベースライン基準だと傾けたときに文字が浮く
        node.setAttribute('text-anchor', 'middle');
        node.setAttribute('dominant-baseline', 'middle');
        node.textContent = char;
        box.appendChild(node);
        return node;
    });

    // getPointAtLength は毎フレーム文字数ぶん呼ぶと重いので、座標を先に表にしておく
    const samples = Math.ceil(total / SAMPLE_RES);
    const xs = new Float32Array(samples + 1);
    const ys = new Float32Array(samples + 1);
    const angles = new Float32Array(samples + 1);

    for (let i = 0; i <= samples; i += 1) {
        const point = path.getPointAtLength(Math.min(i * SAMPLE_RES, total));
        xs[i] = point.x;
        ys[i] = point.y;
    }

    // 角度は前後の点の中心差分から出す。片側だけだと曲率が変わる箇所で角度が跳ねる。
    // 開いたパスなので端は巻き戻さずクランプする
    for (let i = 0; i <= samples; i += 1) {
        const back = Math.max(i - 1, 0);
        const ahead = Math.min(i + 1, samples);
        angles[i] = (Math.atan2(ys[ahead] - ys[back], xs[ahead] - xs[back]) * 180) / Math.PI;
    }

    const speed = total / LOOP_SEC;
    let offset = 0;

    return (delta: number): void => {
        // rAFの初回フレームは now が prev を下回ることがあり delta が負になる。
        // 剰余だけでは負が残って座標表を負インデックスで引いてしまうので、足して二重に正規化する
        offset = (((offset + delta * speed) % total) + total) % total;

        for (let i = 0; i < nodes.length; i += 1) {
            const at = (dists[i] + offset) % total;
            const raw = at / SAMPLE_RES;
            const idx = raw | 0;
            const frac = raw - idx;
            const next = Math.min(idx + 1, samples);
            const x = xs[idx] + (xs[next] - xs[idx]) * frac;
            const y = ys[idx] + (ys[next] - ys[idx]) * frac;

            nodes[i].setAttribute(
                'transform',
                `translate(${x.toFixed(2)} ${y.toFixed(2)}) rotate(${angles[idx].toFixed(1)})`,
            );
        }
    };
};

// 追従が張ったリスナーを外す手続き。非同期遷移のたびに溜まらないよう持っておく
const cleanups: (() => void)[] = [];

/**
 * 帯をスクロールに追わせるものを組む
 *
 * @description 画面に留めるだけなら sticky で足りるが、それだと帯が画面に貼りついた
 *              板に見える。目標へ毎フレーム寄せる形にすると、動かしているあいだは
 *              遅れてついてきて、止めるとふわっと追いつく。
 *              下まで一緒に降りるとロゴとすれ違うので、手前で止めてそこから消していく。
 * @param svg 帯のSVG
 * @param reduced モーションを抑える設定か。遅れだけ外して追従は残す
 * @returns 経過秒を受け取って位置と濃さを更新する関数。組めなければ null
 */
const buildFollower = (svg: SVGSVGElement, reduced: boolean): Runner | null => {
    const box = svg.parentElement;
    const logo = document.querySelector<HTMLElement>('.footer__endLogo');

    if (!box || !logo) {
        return null;
    }

    // 変形を外した素の位置と、止める位置。どちらもページ座標
    let base = 0;
    let stop = 0;
    let stick = 0;
    let lag = 0;
    let pc = false;

    /**
     * 画面幅で持ち方が変わるものを測り直す
     *
     * @description 位置は毎フレーム測るのでここでは扱わない
     * @returns {void}
     */
    const measure = (): void => {
        pc = window.innerWidth >= PC_MIN_WIDTH;
    };

    measure();
    window.addEventListener('resize', measure);
    cleanups.push(() => window.removeEventListener('resize', measure));

    return (delta: number): void => {
        if (!pc) {
            // SPはCSSのstickyに任せるので、PCで書いたぶんを残さない
            if (svg.style.transform !== '' || svg.style.opacity !== '') {
                svg.style.transform = '';
                svg.style.opacity = '';
            }

            lag = 0;

            return;
        }

        // 位置は毎フレーム測る。非同期遷移はページ全体をずらしてanimateするので、
        // 初期化のときに一度だけ測ると遷移ぶんずれた基準のまま固まってしまう。
        // 自分が当てた移動量は既知なので引けば、いつ測っても素の位置が出る
        const sr = svg.getBoundingClientRect();
        const h = sr.height;
        const k = h / DESIGN_SVG_H;

        base = window.scrollY + sr.top - lag;
        stick = DESIGN_STICK_TOP * k;
        stop = window.scrollY + logo.getBoundingClientRect().top - h - DESIGN_LOGO_GAP * k;

        // 止めなかったらどこまで行くか（raw）と、実際に行く先（follow）を分けて持つ。
        // 差がそのまま「行き過ぎたぶん」になるので、消え始めを別に測らなくていい
        const maxFollow = Math.max(0, stop - base);
        const raw = Math.max(0, window.scrollY - (base - stick));
        const follow = Math.min(raw, maxFollow);

        // 0.06/フレームだと画面のリフレッシュレートで速さが変わるので秒あたりに直す
        const rate = reduced ? 1 : 1 - Math.pow(1 - LAG_PER_FRAME, delta * 60);
        lag += (follow - lag) * rate;

        svg.style.transform = `translate3d(0, ${lag.toFixed(1)}px, 0)`;

        const over = clamp01((raw - maxFollow) / Math.max(1, window.innerHeight * FADE_RANGE));
        svg.style.opacity = (1 - ease(over)).toFixed(3);
    };
};

// 走っているrAFループのID。非同期遷移でフッターごと作り直すとき止める
let frame = 0;

// 初期化の世代。document.fonts.ready を待つ間に遷移が起きても、
// 古い初期化が2本目のループを立ち上げないようにする
let generation = 0;

/**
 * ページ内のマーキーを初期化する
 *
 * @param scope 探索範囲。非同期遷移の最中は新旧2つのフッターが同時に居るので、
 *              入ってくる側だけを渡して古い方を動かさないようにする
 */
const initMarquee = async (scope: ParentNode = document): Promise<void> => {
    const mine = generation + 1;
    generation = mine;

    const svgs = [...scope.querySelectorAll<SVGSVGElement>('[data-marquee]')];

    if (svgs.length === 0) {
        return;
    }

    // 文字幅の実測がフォント読み込み前だと狂って、繰り返し数がずれる
    await document.fonts.ready;

    if (mine !== generation) {
        return;
    }

    const runners = svgs
        .map(buildRunner)
        .filter((runner): runner is Runner => runner !== null);

    if (runners.length === 0) {
        return;
    }

    // 動かさない設定でも文字は並べておく
    runners.forEach((run) => run(0));

    // 帯が流れるのは装飾なので抑える設定では止める。ただし追従は残す。
    // 止めるとロゴとすれ違ってしまうので、遅れだけ外して即座に追わせる
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const followers = svgs
        .map((svg) => buildFollower(svg, reduced))
        .filter((follow): follow is Runner => follow !== null);

    if (reduced && followers.length === 0) {
        return;
    }

    let prev = performance.now();

    const tick = (now: number): void => {
        const delta = Math.min((now - prev) / 1000, MAX_DELTA);
        prev = now;
        if (!reduced) {
            runners.forEach((run) => run(delta));
        }

        followers.forEach((follow) => follow(delta));
        frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
};

/**
 * マーキーを止める
 */
const destroyMarquee = (): void => {
    generation += 1;

    if (frame !== 0) {
        cancelAnimationFrame(frame);
        frame = 0;
    }

    cleanups.splice(0).forEach((off) => off());
};

export { initMarquee, destroyMarquee };

// リスナーの引数（Event）がscopeに渡らないよう包む
document.addEventListener('DOMContentLoaded', () => {
    void initMarquee();
});
